import type { ResetSignInState } from 'definitions/password-reset';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { AuthLayout } from './AuthLayout';
import { AuthError, AuthField, AuthHeading, AuthNotice, AuthPrimaryButton, authLinkClass } from './AuthControls';
import { useForm } from 'hooks/useForm';
import { useAuth } from 'hooks/useAuth';
import { trackUmami } from '@/lib/umami/umami';

type StateType = {
  password: string;
  confirmPassword: string;
};

type NotReady = Exclude<ResetSignInState, 'ready'>;

/**
 * A reset never changes an account's status, so a saved password is not always one the
 * person can sign in with yet. Saying "sign in with your new password" there only led to a
 * bare "Unauthorized" on the next screen. The blocked copy does not name the status.
 */
const NOT_READY_COPY: Record<NotReady, { title: string; body: string }> = {
  'confirm-email': {
    title: 'Confirm your email first',
    body: 'Use the link in the email we sent you when you signed up, then sign in with your new password.',
  },
  unavailable: {
    title: "You can't sign in to this account",
    body: 'Contact your admin if you think this is a mistake.',
  },
};

export const ResetPasswordScreen = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notReady, setNotReady] = useState<NotReady | null>(null);
  const { resetPassword } = useAuth();
  const { state, formRef, update } = useForm<StateType>();
  const { password, confirmPassword } = state;

  // The link from the email was followed: the second half of a reset the request started.
  useEffect(() => trackUmami('password-reset-link-opened'), []);

  const onSubmit = async () => {
    if (isDisabled) return;
    setError(null);
    setIsSubmitting(true);
    try {
      const verificationCode = new URL(window.location.toString()).searchParams.get('verificationCode');
      if (!verificationCode) {
        trackUmami('password-reset-failed', { reason: 'no-code' });
        throw new Error('This reset link is no longer valid. Request a new one.');
      }
      const { signIn } = await resetPassword({ password, verificationCode }).catch((error: unknown) => {
        trackUmami('password-reset-failed', { reason: 'rejected' });
        throw error;
      });
      trackUmami('password-reset-completed');
      if (signIn && signIn !== 'ready') {
        // The link is used up either way, so the form gives way to the notice.
        setNotReady(signIn);
        return;
      }
      toast.success('Your password has been reset. Sign in with your new password.');
      navigate('/auth/signin');
    } catch (ex) {
      update({ password: '', confirmPassword: '' });
      const { message } = ex as { message?: string };
      setError(message || 'Could not reset your password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDisabled = isSubmitting || !password || password !== confirmPassword;

  return (
    <AuthLayout
      footer={
        <span>
          {notReady ? 'Back to' : 'Changed your mind?'}{' '}
          <Link to="/auth/signin" className={`${authLinkClass} font-medium`}>
            Sign in
          </Link>
        </span>
      }
    >
      {notReady ? (
        <>
          <AuthHeading title="Password saved" subtitle="Your new password is set." />
          <AuthNotice tone="warn" title={NOT_READY_COPY[notReady].title}>
            {NOT_READY_COPY[notReady].body}
          </AuthNotice>
        </>
      ) : (
        <>
          <AuthHeading title="Set a new password" subtitle="Choose something you have not used here before." />

          <form onSubmit={onSubmit} ref={formRef} className="flex flex-col gap-[18px]">
            {!!error && <AuthError>{error}</AuthError>}

            <AuthField label="New password" type="password" name="password" autoComplete="new-password" />
            <AuthField
              label="Confirm new password"
              type="password"
              name="confirmPassword"
              autoComplete="new-password"
            />

            <AuthPrimaryButton disabled={isDisabled} isPending={isSubmitting}>
              {isSubmitting ? 'Resetting…' : 'Reset password'}
            </AuthPrimaryButton>
          </form>
        </>
      )}
    </AuthLayout>
  );
};
